import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
  Button,
  Chip,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';
import {
  AddCircle,
  Delete,
  Email,
  Person,
  Share,
  School,
} from '@mui/icons-material';
import { useTheme } from '@mui/system';
import AddPlayerDialog from './AddPlayerDialog';
import { toast } from 'react-toastify';
import { paymentStatusMap } from '../../../components/constants';
import axiosInstance from '../../../services/axiosInstance';
import TotalPaymentDialog from './TotalPaymentDialog';

const PlayersManagement = ({
  user,
  booking,
  setBooking,
  players,
  setPlayers,
  members,
  isBookingOwner,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [newPlayer, setNewPlayer] = useState({
    isMember: true,
    memberId: '',
    nonMemberEmail: '',
  });
  const [loadingLinks, setLoadingLinks] = useState({}); // Track loading state for each player
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const bookingDate = new Date(booking.dateBooked);

  const getBookingStartTime = () => {
    const [startHour, startMinute] = booking.timeBooked
      .split(' - ')[0]
      .split(':'); // Split the time correctly
    if (!booking.dateBooked) {
      console.error('Selected date is undefined');
      return null;
    }

    return new Date(
      bookingDate.getFullYear(), // Extract year
      bookingDate.getMonth(), // Extract month
      bookingDate.getDate(), // Extract day
      parseInt(startHour), // Extract hour
      parseInt(startMinute) // Extract minute
    );
  };
  const isBeforeBookingTime = new Date() < getBookingStartTime();
  const hasMultiplePlayers = Array.isArray(players) && players.length > 1;

  const getPlayerPaymentTooltip = () => {
    if (isBeforeBookingTime) {
      return 'El pago solo se puede realizar después de la hora de la reserva';
    }

    if (!hasMultiplePlayers) {
      return 'Añade al menos un jugador para poder realizar el pago.';
    }

    return '';
  };

  const handleDialogOpen = () => setDialogOpen(true);

  const handleOpenPaymentDialog = player => {
    setSelectedPlayer(player);
    setPaymentDialogOpen(true);
  };

  const handleClosePaymentDialog = () => {
    setSelectedPlayer(null);
    setPaymentDialogOpen(false);
  };

  const handleGeneratePaymentLink = async playerIndex => {
    const player = players[playerIndex];
    if (!player || player.stripePaymentLinkUrl) {
      toast.info('El enlace de pago ya está generado.');
      return;
    }

    setLoadingLinks(prev => ({ ...prev, [playerIndex]: true }));

    try {
      const response = await axiosInstance.post(
        '/users/booking/createPaymentLink',
        {
          bookingId: booking._id,
          playerIndex,
        }
      );

      const url = response.url;

      if (url) {
        // Actualizar el jugador con el enlace
        const updatedPlayers = [...players];
        updatedPlayers[playerIndex] = { ...player, stripePaymentLinkUrl: url };
        setPlayers(updatedPlayers);

        toast.success('Enlace de pago generado.');
      } else {
        toast.error('Error al generar el enlace de pago.');
      }
    } catch (error) {
      console.error('Error al generar enlace de pago:', error);
      toast.error('No se pudo generar el enlace de pago.');
    } finally {
      setLoadingLinks(prev => ({ ...prev, [playerIndex]: false }));
    }
  };

  const handleShareViaWhatsApp = paymentLink => {
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(
      `Aquí tienes el enlace de pago para la reserva: ${paymentLink}`
    )}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleSendEmail = (email, paymentLink) => {
    const mailtoUrl = `mailto:${email}?subject=Enlace%20de%20pago&body=${encodeURIComponent(
      `Aquí tienes el enlace de pago para la reserva: ${paymentLink}`
    )}`;
    window.open(mailtoUrl, '_blank');
  };

  const handleCopyPaymentLink = paymentLink => {
    navigator.clipboard.writeText(paymentLink);
    toast.success('Enlace copiado al portapapeles.');
  };

  const getEntityId = entity => {
    const id = entity?._id ?? entity?.id ?? entity;
    return id == null ? null : String(id);
  };

  const isFamilyPlayer = player => {
    const relationship = String(player?.nonMemberName || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();

    return relationship === 'conyuge' || relationship === 'hijo';
  };

  const isBookingCreatorPlayer = player =>
    getEntityId(player?.memberId) === getEntityId(booking.userBooked) &&
    !isFamilyPlayer(player);

  const isCurrentUserPlayer = player =>
    getEntityId(player?.memberId) === getEntityId(user);

  const canRemovePlayer = player =>
    booking.paymentStatus !== 'P' &&
    player?.paymentStatus !== 'P' &&
    !booking.isLeagueMatch &&
    !isBookingCreatorPlayer(player) &&
    (isBookingOwner || isCurrentUserPlayer(player));

  const handleRemovePlayer = async playerIndex => {
    const player = players[playerIndex];

    if (!player || !canRemovePlayer(player)) {
      toast.error('No tienes permisos para eliminar a este jugador.');
      return;
    }

    const updatedPlayers = players.filter((_, index) => index !== playerIndex);

    try {
      // Realiza la llamada al backend para actualizar la reserva
      const response = await axiosInstance.put(
        `/users/booking/${booking._id}`,
        {
          players: updatedPlayers, // Envía la lista actualizada al backend
        }
      );

      if (response.success) {
        toast.success('Jugador eliminado de la reserva.');
        setBooking(response.booking);
        setPlayers(response.booking.players);
      } else {
        toast.error(
          response.data.msg ||
            'No se pudo actualizar la reserva en el servidor.'
        );
      }
    } catch (error) {
      console.error('Error al actualizar la reserva:', error);
      toast.error(
        error.response?.data?.message ||
          'Hubo un error al intentar eliminar el jugador.'
      );
    }
  };

  return (
    <Box mt={4}>
      <Typography variant="h5" textAlign="center" gutterBottom mb={4}>
        Invita a Jugadores
      </Typography>
      <Grid container spacing={isMobile ? 2 : 4}>
        {players && players.length > 0 ? (
          players.map((player, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card
                sx={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: 'center',
                  padding: 2,
                  boxShadow: 3,
                  minHeight: '120px',
                  gap: isMobile ? 1 : 2,
                }}
              >
                {/* Foto de perfil */}
                <Avatar
                  src={player.isMember ? player.memberId?.image : ''}
                  alt={
                    player.isMember || player.memberId
                      ? (['Cónyuge', 'Hijo'].includes(player.nonMemberName)
                          ? player.nonMemberName
                          : `${player.memberId?.name || ''} ${player.memberId?.surname || ''}`.trim() ||
                            player.nonMemberName) ||
                        `${player.nonMemberName || ''} ${player.nonMemberSurname || ''}`.trim() ||
                        'Jugador Socio'
                      : `${player.nonMemberName || ''} ${player.nonMemberSurname || ''}`.trim() ||
                        player.nonMemberEmail ||
                        'Jugador No Socio'
                  }
                  sx={{ width: 56, height: 56 }}
                >
                  {!player.isMember && <Person />}
                </Avatar>

                {/* Información del jugador */}
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    textAlign: isMobile ? 'center' : 'left',
                  }}
                >
                  <Typography variant="h6" noWrap>
                    {player.isMember || player.memberId
                      ? (['Cónyuge', 'Hijo'].includes(player.nonMemberName)
                          ? player.nonMemberName
                          : `${player.memberId?.name || ''} ${player.memberId?.surname || ''}`.trim() ||
                            player.nonMemberName) ||
                        `${player.nonMemberName || ''} ${player.nonMemberSurname || ''}`.trim() ||
                        player.nonMemberEmail ||
                        'Jugador Socio'
                      : `${player.nonMemberName || ''} ${player.nonMemberSurname || ''}`.trim() ||
                        player.nonMemberEmail ||
                        'Jugador No Socio'}
                  </Typography>

                  {/* Destacar si es un jugador universitario */}
                  {player.isUca && (
                    <Chip
                      icon={<School />}
                      label="Jugador UCA"
                      color="primary"
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  )}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: isMobile ? 'center' : 'start',
                      gap: 1,
                      mt: 1,
                    }}
                  >
                    <Typography variant="body1" color="text.primary">
                      {player.paymentStatus === 'P'
                        ? player.amountPaid
                        : player.amount}{' '}
                      €
                    </Typography>
                    <Chip
                      label={
                        paymentStatusMap[player.paymentStatus]?.label ||
                        'Desconocido'
                      }
                      color={
                        paymentStatusMap[player.paymentStatus]?.color ||
                        'default'
                      }
                      size="small"
                    />
                  </Box>
                </Box>

                {/* Acciones del jugador */}
                {booking.paymentStatus !== 'P' && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: isMobile ? 'row' : 'column',
                      alignItems: isMobile ? 'center' : 'flex-end',
                      gap: 1,
                    }}
                  >
                    {/* Botón para generar o copiar enlace de pago */}
                    {!player.isMember &&
                      !booking.isLeagueMatch &&
                      player.paymentStatus !== 'P' && (
                        <>
                          {loadingLinks[index] ? (
                            <CircularProgress size={24} />
                          ) : player.stripePaymentLinkUrl ? (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              <Tooltip title="Copiar enlace de pago">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  fullWidth={isMobile}
                                  onClick={() =>
                                    handleCopyPaymentLink(
                                      player.stripePaymentLinkUrl
                                    )
                                  }
                                >
                                  Copiar enlace de pago
                                </Button>
                              </Tooltip>
                              <Tooltip title="Compartir por WhatsApp">
                                <IconButton
                                  color="success"
                                  onClick={() =>
                                    handleShareViaWhatsApp(
                                      player.stripePaymentLinkUrl
                                    )
                                  }
                                >
                                  <Share />
                                </IconButton>
                              </Tooltip>
                              {player.nonMemberEmail && (
                                <Tooltip title="Enviar por correo">
                                  <IconButton
                                    color="info"
                                    onClick={() =>
                                      handleSendEmail(
                                        player.nonMemberEmail,
                                        player.stripePaymentLinkUrl
                                      )
                                    }
                                  >
                                    <Email />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          ) : (
                            <Tooltip
                              title={
                                isBeforeBookingTime
                                  ? 'El enlace solo se puede generar después de la hora de la reserva'
                                  : 'Genera un enlace para que este jugador pague su parte'
                              }
                            >
                              <Button
                                size="small"
                                variant="outlined"
                                fullWidth={isMobile}
                                onClick={() => handleGeneratePaymentLink(index)}
                                disabled={isBeforeBookingTime}
                              >
                                Obtener Enlace de Pago
                              </Button>
                            </Tooltip>
                          )}
                        </>
                      )}

                    {/* Botón para socios */}
                    {(player.isMember || booking.isLeagueMatch) &&
                      (player.memberId?._id === user.id ||
                        player.memberId?.role === 'vip') && // Permitir que VIPs vean el botón
                      player.paymentStatus !== 'P' &&
                      (() => {
                        const [startHour, startMinute] = booking.timeBooked
                          .split(' - ')[0]
                          .split(':');
                        bookingDate.setHours(
                          parseInt(startHour, 10),
                          parseInt(startMinute, 10),
                          0,
                          0
                        );

                        const playerPaymentTooltip = getPlayerPaymentTooltip();

                        return (
                          <Tooltip title={playerPaymentTooltip}>
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                fullWidth={isMobile}
                                onClick={() =>
                                  handleOpenPaymentDialog({ ...player, index })
                                }
                                disabled={Boolean(playerPaymentTooltip)}
                              >
                                Pagar Parte ({player.amount - player.amountPaid}
                                €)
                              </Button>
                            </span>
                          </Tooltip>
                        );
                      })()}

                    {/* Botón para eliminar jugador */}
                    {canRemovePlayer(player) && (
                      <Tooltip title="Eliminar jugador">
                        <IconButton
                          onClick={() => handleRemovePlayer(index)}
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                )}
              </Card>
            </Grid>
          ))
        ) : (
          <Typography variant="body1" color="text.secondary">
            No hay jugadores disponibles.
          </Typography>
        )}
        {/* Mostrar tarjeta de "Añadir Jugador" solo si hay menos de 4 jugadores */}
        {!isBookingOwner ||
        booking.paymentStatus === 'P' ||
        booking.isLeagueMatch
          ? null
          : players.length < 4 && (
              <Grid item xs={12} sm={6}>
                <Card
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: 3,
                    padding: 2,
                    minHeight: '120px',
                    cursor: 'pointer',
                    transition: 'transform 0.2s',
                    '&:hover': { transform: 'scale(1.05)' },
                  }}
                  onClick={handleDialogOpen}
                >
                  <AddCircle
                    color="primary"
                    sx={{ fontSize: 40, marginRight: 2 }}
                  />
                  <Typography variant="h6" color="primary">
                    Añadir Jugador
                  </Typography>
                </Card>
              </Grid>
            )}
      </Grid>
      <AddPlayerDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        members={members}
        booking={booking}
        setBooking={setBooking}
        players={players}
        setPlayers={setPlayers}
        newPlayer={newPlayer}
        setNewPlayer={setNewPlayer}
      />
      {/* Dialogo de pago */}
      <TotalPaymentDialog
        open={paymentDialogOpen}
        onClose={handleClosePaymentDialog}
        bookingId={booking._id}
        totalAmount={selectedPlayer?.amount}
        player={selectedPlayer}
      />
    </Box>
  );
};

export default PlayersManagement;
